import {
    Modal,
    Stack,
    Title,
    Text,
    Group,
    Button,
    Paper,
    ThemeIcon,
    Divider,
    Box,
    useMantineTheme
} from '@mantine/core';
import { IconCurrencyDollar, IconArrowRight, IconCurrencyRupee, IconAlertCircle } from '@tabler/icons-react';
import { formatCurrencyByCode, CURRENCY } from '../../utils/currencyUtils';

interface PaymentCurrencyDisclaimerModalProps {
    opened: boolean;
    onClose: () => void;
    onProceed: () => void;
    amountUSD: number;
    amountLKR: number;
    exchangeRate: number;
    isMobile?: boolean;
}

/**
 * Component to display a disclaimer modal before redirecting to payment gateway
 * Explains the currency conversion between USD (shown in the app) and LKR (shown in payment gateway)
 */
const PaymentCurrencyDisclaimerModal = ({
    opened,
    onClose,
    onProceed,
    amountUSD,
    amountLKR,
    exchangeRate,
    isMobile = false
}: PaymentCurrencyDisclaimerModalProps) => {
    const theme = useMantineTheme();

    // Format currency with appropriate symbol
    const formatUSD = (amount: number) => {
        return formatCurrencyByCode(amount, CURRENCY.USD);
    };

    const formatLKR = (amount: number) => {
        return formatCurrencyByCode(amount, CURRENCY.LKR);
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <Group>
                    <ThemeIcon color="blue" size="md" variant="light" radius="xl">
                        <IconAlertCircle size={16} />
                    </ThemeIcon>
                    <Title order={4}>Payment Currency Information</Title>
                </Group>
            }
            centered
            size={isMobile ? "95%" : "md"}
            padding="lg"
        >
            <Stack>
                {/* Alert section */}
                <Paper withBorder p="md" radius="md" bg="blue.0">
                    <Text fw={500} mb="xs">
                        You are about to be redirected to our payment gateway.
                    </Text>
                    <Text size="sm">
                        Please note that while our app displays prices in US Dollars (USD),
                        the payment gateway will show the amount in Sri Lankan Rupees (LKR).
                    </Text>
                </Paper>

                {/* Currency conversion visualization */}
                <Paper withBorder p="md" radius="md">
                    <Text fw={600} mb="md" ta="center">Your payment will be processed as follows:</Text>

                    <Group justify="center" mb="md">
                        <Stack align="center" gap="xs">
                            <ThemeIcon size="xl" radius="xl" color="blue">
                                <IconCurrencyDollar size={24} />
                            </ThemeIcon>
                            <Text fw={700} size="lg">{formatUSD(amountUSD)}</Text>
                            <Text size="xs" c="dimmed">Amount shown in app</Text>
                        </Stack>

                        <Box mt={-10}>
                            <IconArrowRight size={24} color={theme.colors.gray[5]} />
                        </Box>

                        <Stack align="center" gap="xs">
                            <ThemeIcon size="xl" radius="xl" color="green">
                                <IconCurrencyRupee size={24} />
                            </ThemeIcon>
                            <Text fw={700} size="lg">{formatLKR(amountLKR)}</Text>
                            <Text size="xs" c="dimmed">Amount in payment gateway</Text>
                        </Stack>
                    </Group>

                    <Divider my="md" />

                    <Text size="sm" ta="center">
                        Current exchange rate: <b>1 USD = {exchangeRate.toFixed(2)} LKR</b>
                    </Text>
                </Paper>

                {/* Additional information */}
                <Text size="sm" c="dimmed">
                    Your card issuer may apply their own exchange rate and fees if your card is not denominated in LKR.
                </Text>

                {/* Action buttons */}
                <Group justify="flex-end" mt="md">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={onProceed}>
                        Proceed to Payment
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default PaymentCurrencyDisclaimerModal;
